import classNames from 'classnames';
import { CheckIcon, ChevronDown, X } from 'lucide-react';
import { ReactElement, useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';

type Option = { label: string; value: string | boolean; description?: string; searchText?: string };

export default function Combobox({
	options,
	defaultValue,
	onSelectedChange,
	title,
	disabled,
	className = '',
	containerClassName = '',
	Icon,
	placeholder,
	searchPlaceholder,
	floatingLabel,
	isDefaultValueNull = true,
	emptyMessage = 'No results found.',
	inlineDropdown = false
}: {
	options: Option[];
	defaultValue?: string | boolean;
	onSelectedChange: (selected: string | boolean | null) => any;
	title?: string;
	disabled?: boolean;
	className?: string;
	containerClassName?: string;
	Icon?: ReactElement;
	placeholder?: string;
	searchPlaceholder?: string;
	floatingLabel?: string;
	isDefaultValueNull?: boolean;
	emptyMessage?: string;
	inlineDropdown?: boolean;
}) {
	const [open, setOpen] = useState(false);
	const [selectedValue, setSelectedValue] = useState<string | boolean | undefined>(defaultValue);
	const [searchTerm, setSearchTerm] = useState('');
	const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
	const containerRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);
	const dropdownRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		setSelectedValue(defaultValue);
	}, [defaultValue]);

	useLayoutEffect(() => {
		if (open && !inlineDropdown && triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 200) });
		}
	}, [inlineDropdown, open]);

	const selectedOption = options.find((opt) => opt.value === selectedValue);
	const selectedLabel = selectedOption?.label;

	const filteredOptions = options.filter((option) => {
		const searchableText = option.searchText || `${option.label} ${option.description || ''}`;
		return searchableText.toLowerCase().includes(searchTerm.trim().toLowerCase());
	});

	const handleSelect = useCallback((option: Option) => {
		if (option.value === selectedValue && isDefaultValueNull) {
			setSelectedValue(undefined);
			onSelectedChange(null);
		} else {
			setSelectedValue(option.value);
			onSelectedChange(option.value);
		}
		setSearchTerm('');
		setOpen(false);
	}, [selectedValue, isDefaultValueNull, onSelectedChange]);

	const handleToggle = () => {
		if (disabled) return;
		setOpen(!open);
	};

	useEffect(() => {
		if (open) {
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [open]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const isInsideContainer = containerRef.current?.contains(target);
			const isInsideDropdown = dropdownRef.current?.contains(target);
			if (!isInsideContainer && !isInsideDropdown) {
				setOpen(false);
				setSearchTerm('');
			}
		};

		const handleScroll = () => {
			setOpen(false);
			setSearchTerm('');
		};

		if (open) {
			document.addEventListener('mousedown', handleClickOutside);
			if (!inlineDropdown) window.addEventListener('scroll', handleScroll, true);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			if (!inlineDropdown) window.removeEventListener('scroll', handleScroll, true);
		};
	}, [inlineDropdown, open]);

	return (
		<div className={classNames(containerClassName, 'space-y-1 relative')} ref={containerRef}>
			{title && <h3 className="font-medium tracking-tight text-neutral-300">{title}</h3>}
			<div
				className={classNames('relative w-full rounded-lg', {
					'border border-neutral-800 bg-neutral-925 light:bg-neutral-950': floatingLabel
				})}
			>
				<button
					ref={triggerRef}
					className={classNames(
						'inline-flex w-full shrink-0 select-none items-center justify-between gap-2 rounded-lg text-nowrap outline-none transition-colors hover:bg-primary-600 disabled:pointer-events-none disabled:opacity-60',
						floatingLabel
							? 'h-12 bg-transparent px-3 text-base'
							: 'h-10 bg-neutral-1000 px-3 text-sm light:bg-neutral-900/70',
						{
							'ring-[2.5px] ring-primary-500': open && floatingLabel,
							'font-normal text-neutral-400': typeof selectedValue === 'undefined',
							'font-medium text-neutral-100': typeof selectedValue !== 'undefined',
							'font-normal text-neutral-300': !selectedValue && !floatingLabel
						},
						className
					)}
					type="button"
					disabled={disabled}
					onClick={handleToggle}
					aria-haspopup="listbox"
					aria-expanded={open}
				>
					<div className="flex min-w-0 items-center gap-1 truncate">
						{Icon && <div className="shrink-0">{Icon}</div>}
						{selectedOption?.description ? (
							<div className="flex min-w-0 flex-col items-start leading-tight">
								<span className="max-w-full truncate">{selectedOption.label}</span>
								<span className="max-w-full truncate text-xs font-normal text-neutral-400">{selectedOption.description}</span>
							</div>
						) : (
							<span className="truncate">{selectedLabel || placeholder}</span>
						)}
					</div>
					<div className="flex items-center justify-end gap-0.5">
						{selectedValue !== undefined && (
							<button
								type="button"
								className="shrink-0 rounded p-0.5 hover:bg-neutral-800/50"
								onClick={(e) => {
									e.stopPropagation();
									setSelectedValue(undefined);
									onSelectedChange(null);
									setOpen(false);
								}}
							>
								<X className="size-3.5 text-neutral-400 hover:text-neutral-200" strokeWidth={2.5} />
							</button>
						)}
						<ChevronDown className="size-4 shrink-0 !text-neutral-100" strokeWidth={2.5} />
					</div>
				</button>
				{floatingLabel && (
					<label className="absolute -top-2 left-2 max-w-[90%] overflow-hidden overflow-ellipsis text-nowrap rounded bg-neutral-1000 px-1 text-xs font-medium text-primary-200/80 light:bg-primary-300 light:text-primary-900">
						{floatingLabel}
					</label>
				)}
			</div>

			{open && (
				<div
					ref={dropdownRef}
					className={classNames(
						'z-[120] overflow-hidden rounded-lg border border-primary-600 bg-primary-600 shadow-lg',
						inlineDropdown ? 'absolute left-0 top-[calc(100%+4px)] w-full' : 'fixed w-max max-w-sm'
					)}
					style={inlineDropdown ? undefined : { top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width }}
				>
					<div className="flex h-10 items-center gap-2 border-b border-primary-500/30 px-3">
						<input
							ref={inputRef}
							className="flex h-full w-full bg-transparent text-sm text-primary-50 placeholder:text-primary-300 outline-none"
							placeholder={searchPlaceholder || (floatingLabel ? `Buscar ${floatingLabel.toLowerCase()}...` : 'Buscar...')}
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Escape') {
									setOpen(false);
									setSearchTerm('');
								}
								e.stopPropagation();
							}}
						/>
					</div>
					<div
						ref={listRef}
						className="max-h-64 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-primary-600 [&::-webkit-scrollbar-thumb]:bg-primary-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-primary-300"
						style={{
							scrollbarColor: 'hsl(var(--twc-primary-400)) hsl(var(--twc-primary-600))'
						}}
						onWheel={(e) => e.stopPropagation()}
						onTouchMove={(e) => e.stopPropagation()}
					>
						{filteredOptions.length === 0 ? (
							<div className="px-3 py-2 text-sm text-primary-300">{emptyMessage}</div>
						) : (
							<div className="py-1">
								{filteredOptions.map((option, idx) => (
									<button
										key={idx}
										className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-primary-50 hover:bg-primary-500 focus:bg-primary-500 focus:outline-none"
										onClick={() => handleSelect(option)}
									>
										<span className="flex min-w-0 flex-1 flex-col">
											<span className="truncate">{option.label}</span>
											{option.description && <span className="truncate text-xs text-primary-300">{option.description}</span>}
										</span>
										<CheckIcon
											className={classNames(
												'size-4 shrink-0',
												selectedValue === option.value ? 'opacity-100' : 'opacity-0'
											)}
											strokeWidth={2.5}
										/>
									</button>
								))}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
}

